import { Heart, Users, BedDouble, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AMENITY_LABELS } from "@/data/site";
import { formatEUR } from "@/lib/booking";

export default function PropertyCard({ property }) {
  const { favorites, toggleFavorite, scrollToId, setBookingPrefill } = useApp();
  const navigate = useNavigate();
  const fav = favorites.includes(property.id);

  const verify = () => {
    setBookingPrefill({ propertyId: property.id, destination: property.name });
    scrollToId("booking");
  };

  return (
    <article
      data-testid={`property-card-${property.slug}`}
      className="group bg-white border border-brand-ivory rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col"
    >
      <div className="relative h-60 overflow-hidden">
        <img src={property.featuredImage} alt={property.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-brand-olive text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {property.badge}
        </span>
        <button
          data-testid={`fav-${property.slug}`}
          onClick={() => toggleFavorite(property.id)}
          aria-label={fav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Heart className={`w-4 h-4 ${fav ? "fill-brand-gold text-brand-gold" : "text-brand-brown"}`} />
        </button>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-heading text-2xl text-brand-black leading-tight">{property.name}</h3>
        </div>
        <p className="text-xs uppercase tracking-wide text-brand-sage mt-1">{property.type} · {property.city}</p>
        {property.villaBadge && (
          <span className="inline-flex mt-2 w-fit text-xs bg-brand-gold/15 text-brand-brown px-2.5 py-1 rounded-full" data-testid="villa-badge">{property.villaBadge}</span>
        )}
        <p className="text-sm text-brand-brown mt-3 leading-relaxed line-clamp-3">{property.shortDescription}</p>

        <div className="flex items-center gap-4 mt-4 text-sm text-brand-black/70">
          <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-brand-sage" /> Fino a {property.maxGuests}</span>
          {property.bedrooms ? <span className="inline-flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-brand-sage" /> {property.bedrooms} {property.category === "bnb" ? "suite" : property.bedrooms === 1 ? "camera" : "camere"}</span> : null}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-4">
          {property.amenities.slice(0, 4).map((a) => (
            <span key={a} className="text-xs bg-brand-ivory text-brand-brown px-2.5 py-1 rounded-full">{AMENITY_LABELS[a] || a}</span>
          ))}
        </div>

        <div className="mt-auto pt-5">
          <span className="text-sm text-brand-brown">Verifica disponibilità e tariffe</span>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button data-testid={`discover-${property.slug}`} onClick={() => navigate(`/strutture/${property.slug}`)} className="bg-brand-olive text-white hover:bg-brand-black transition-colors rounded-full py-2.5 text-sm font-medium">
            Scopri la struttura
          </button>
          <button data-testid={`verify-${property.slug}`} onClick={verify} className="border border-brand-olive text-brand-olive hover:bg-brand-ivory transition-colors rounded-full py-2.5 text-sm font-medium">
            Verifica disponibilità
          </button>
        </div>
      </div>
    </article>
  );
}
